<template>

    <div>
        <ul class="top-help-block">
            <li class="flex items-center">
                <svg xmlns="http://www.w3.org/2000/svg" class="h-5 w-5 mr-1 text-indigo-500" fill="none" viewBox="0 0 24 24" stroke="currentColor" focusable="false" role="img" aria-labelledby="Is a Scheduled Report">
                    <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
                </svg>
                scheduled report
            </li>
        </ul>

        <table class="responsive-table text-sm shadow">
            <thead>
            <tr class="uppercase text-xs">
                <th class="text-left" colspan="2">Report Name / Parameters</th>
                <th>Status</th>
                <th class="hidden lg:table-cell">Progress</th>
                <th>&nbsp;</th>
            </tr>
            </thead>
            <tbody>
            <tr class="colored-row table-row relative" v-for="job in value.index_data.data">
                <div class="hidden lg:flex absolute z-10 bottom-0">
                    <ul class="flex text-xs pb-3 ">
                        <li class="ml-6 uppercase font-medium text-gray-600">Parameters</li>
                        <li class="flex font-bold items-center ml-2 rounded-xl bg-indigo-700 text-indigo-100 px-1 shadow" v-for="(param) in job.formatted_parameters">
                            {{ param.name }} {{ param.value }}
                        </li>
                    </ul>
                </div>
                <td class="w-4/6 lg:w-2/6 params">
                    <div class="block flex items-center font-medium">
                        {{ job.report_name }}
                        <svg xmlns="http://www.w3.org/2000/svg" class="h-5 w-5 ml-1 text-indigo-500" fill="none" viewBox="0 0 24 24" stroke="currentColor" focusable="false" role="img" aria-labelledby="Scheduled Report" v-if="job.schedule_id">
                            <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
                        </svg>
                    </div>
                    <div class="text-xs font-normal text-gray-600">
                        Requested on {{ formatDate(job.created_at) }} by {{ job.user_name }} <span v-if="job.brand_name">({{ job.account_name }})</span>
                    </div>
                </td>
                <td class="w-1/6 params text-center">
                <span class="status-box" :class="jobStatusBox(job.status)">
                    {{ job.status }}
                </span>
                </td>
                <td class="hidden lg:table-cell w-2/6 params">
                    <div class="pt-1 w-full" v-if="(job.status === 'complete' || job.status === 'pending' || job.status === 'scheduled') && job.total > 0">
                        <div class="flex mb-2 items-center">
                            <div class="flex-1 justify-start">
                            <span class="text-xs font-medium inline-block py-1 px-2 uppercase rounded-full"
                                  :class="{ 'text-gray-800 bg-gray-200': isJobComplete(job.status), 'text-error-600 bg-error-100': !isJobComplete(job.status) }">
                                {{ job.processed.toLocaleString() }} / {{ job.total.toLocaleString() }}
                            </span>
                            </div>
                            <div class="flex justify-end">
                            <span class="text-xs font-medium inline-block" :class="{ 'text-brand-800': isJobComplete(job.status), 'text-error-600': !isJobComplete(job.status) }">
                                {{ computeCompletionPercentage(job.processed, job.total) }} %
                            </span>
                            </div>
                        </div>
                        <div class="overflow-hidden h-2 mb-4 text-xs flex rounded" :class="{ 'bg-gray-200': isJobComplete(job.status), 'bg-error-200': !isJobComplete(job.status) }">
                            <div :style="`width:${computeCompletionPercentage(job.processed, job.total)}%`"
                                 class="shadow-none flex flex-col text-center whitespace-nowrap text-white justify-center"
                                 :class="{ 'bg-error-900': isJobComplete(job.status), 'bg-error-500': !isJobComplete(job.status) }"
                            ></div>
                        </div>
                    </div>
                    <div class="text-center p-3" v-else-if="job.status === 'failed'">
                        <p class="text-pink-600 font-semibold">
                            Report Failed
                        </p>
                        <p class="font-normal text-gray-600 text-xs">
                            The report failed to process, please contact support.
                        </p>
                    </div>
                    <div class="text-center p-3" v-else-if="job.status === 'complete'">
                        <p class="text-pink-600 font-semibold">
                            Report contained no results
                        </p>
                        <p class="font-normal text-gray-600 text-xs">
                            Please try altering the report parameters to include a wider range of results.
                        </p>
                    </div>
                </td>
                <td class="w-1/6 params text-right">
                    <ConfirmationModal :id="`exception-${job.uuid}`" button_text="Show Exception" button_classes="btn-outline inline-block mr-2 text-sm" :show_confirm="false" v-if="job.status === 'failed'">
                        <h2 class="font-bold text-lg mb-0">Report Error</h2>

                        <p class="mb-3 text-sm">
                            There was an error running this report, please see below for details:-
                        </p>

                        <p class="mb-3 text-sm text-error-700 break-all p-3 border border-error-200">
                            {{ job.exception }}
                        </p>

                    </ConfirmationModal>
                    <ConfirmationModal :id="`results-${job.uuid}`" button_text="Download Results" button_classes="btn justify-center text-sm" :show_confirm="false" @openModal="exportReport(job.uuid)" v-if="isJobComplete(job.status) && job.total > 0">
                        <div v-if="value.export_report_links">
                            <h2 class="font-bold text-lg mb-0">{{ value.export_report_links.name }}</h2>

                            <p class="mb-3 text-sm">
                                Requested on {{ formatDate(value.export_report_links.date) }}
                            </p>

                            <p class="text-gray-600 text-sm my-3">
                                This report contains <span class="font-bold text-brand-blue-900">{{ value.export_report_links.total.toLocaleString() }}</span> results and has been split into
                                <span class="font-bold text-brand-blue-900">{{ value.export_report_links.urls.length.toLocaleString() }}</span> file(s). Each file contains a chunk of
                                <span class="font-bold text-brand-blue-900">{{ value.export_report_links.chunk_limit.toLocaleString() }}</span> results.
                            </p>

                            <div class="my-3 h-56 overflow-y-scroll">
                                <div class="flex" v-for="file in value.export_report_links.urls">
                                    <div class="flex-1 py-3 w-2/3 flex items-center">
                                        <svg xmlns="http://www.w3.org/2000/svg" class="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor" focusable="false" role="img" aria-label="See Download Report">
                                            <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M9 17v-2m3 2v-4m3 4v-6m2 10H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                                        </svg>
                                        <span class="text-gray-700 text-sm ml-2 font-medium">{{ file.name }}</span>
                                    </div>
                                    <div class="py-3 w-1/3 text-right">
                                        <a :href="file.url" class="btn text-sm">Download Report</a>
                                    </div>
                                </div>
                            </div>

                        </div>
                        <Loading v-else></Loading>
                    </ConfirmationModal>
                    <span v-else></span>
                </td>
            </tr>

            </tbody>
        </table>
    </div>

</template>

<script>
import ConfirmationModal from "../Support/ConfirmationModal";
import Loading from "../Support/Loading";
import {FormattingMixin} from "../../support/mixins";

export default {
    name: "ReportJobIndex",
    mixins: [FormattingMixin],
    props: {
        value: {
            required: true
        },
    },
    components: {
        ConfirmationModal,
        Loading
    },
    methods: {
        jobStatusBox(status) {
            return {
                'status-open': status === 'scheduled',
                'status-pending': status === 'running',
                'status-cancelled': status === 'failed',
                'status-complete': status === 'complete',
            }
        },
        computeCompletionPercentage(processed, total) {
            let percentage = (processed / total);

            return Math.round(percentage * 100);
        },
        isJobComplete(status) {
            return status === 'complete';
        },
        pillClass(status) {
            return {
                'bg-gray-900': status === 'scheduled',
                'bg-amber-500': status === 'running',
                'bg-emerald-500': status === 'complete',
                'bg-error-500': status === 'failed',
            }
        },
        exportReport(uuid) {
            this.$emit('exportReport', uuid);
        },
    }
}
</script>


